import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, ArrowRight, Vault, HeartHandshake, FolderArchive, BarChart3, BookOpen, Landmark, Compass, Sparkles, CalendarCheck, LifeBuoy, Settings2 } from "lucide-react";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "./ui/command";
import { useSecurity } from "../context/SecurityContext";
import { fetchDocuments } from "../lib/api";
import { searchLocal } from "../lib/localIndex";
import { usePrivateMode } from "./PrivateModeRow";
import { ALL_FEATURES, JOURNEYS, featureHref } from "../lib/guideCatalog";
import { searchHelp } from "../lib/helpArticles";

const PAGES = [
  { to: "/vault", label: "Vault", Icon: Vault }, { to: "/planning", label: "Planning", Icon: HeartHandshake }, { to: "/library", label: "Library", Icon: BookOpen },
  { to: "/accounts", label: "Accounts", Icon: Landmark }, { to: "/reports", label: "Reports", Icon: BarChart3 }, { to: "/tax-pack", label: "Tax pack", Icon: FolderArchive },
  { to: "/checkup", label: "Yearly check-up", Icon: CalendarCheck }, { to: "/guide", label: "Guide", Icon: Compass }, { to: "/help", label: "Help & knowledge", Icon: LifeBuoy }, { to: "/admin", label: "Admin", Icon: Settings2 }, { to: "/pricing", label: "Plans", Icon: Sparkles },
];

export const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { member } = useSecurity();
  const role = member?.role || "owner";
  const [q, setQ] = useState("");
  const feats = useMemo(() => ALL_FEATURES.filter((f) => f.roles.includes(role)), [role]);
  const privateMode = usePrivateMode();
  const { data: docs } = useQuery({ queryKey: ["cmdk-docs", q, privateMode], queryFn: () => (privateMode ? searchLocal(q, 6) : fetchDocuments({ search: q })), enabled: open && q.trim().length >= 2, staleTime: 10000 });

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const go = (to) => {
    onOpenChange(false);
    navigate(to);
  };
  const journeyLabel = (id) => JOURNEYS.find((j) => j.id === id)?.label;
  const help = useMemo(() => (q.trim().length >= 2 ? searchHelp(q, 4) : []), [q]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput data-testid="cmdk-input" placeholder="Type a feature, page or document… e.g. beneficiary, scan, insurance" value={q} onValueChange={setQ} />
      <CommandList data-testid="cmdk-list" className="max-h-[60vh]">
        <CommandEmpty>Nothing matches — try another word.</CommandEmpty>
        <CommandGroup heading="Features">
          {feats.map((f) => (
            <CommandItem key={f.id} value={`${f.title} ${f.what} ${journeyLabel(f.journey)}`} data-testid={`cmdk-feature-${f.id}`} onSelect={() => go(featureHref(f))} className="gap-3">
              <f.Icon className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0"><div className="text-sm">{f.title}</div><div className="text-[11px] text-muted-foreground truncate">{f.what}</div></div>
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{journeyLabel(f.journey)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Pages">
          {PAGES.map((p) => (
            <CommandItem key={p.to} value={`page ${p.label}`} data-testid={`cmdk-page-${p.to.replace(/\//g, "") || "home"}`} onSelect={() => go(p.to)} className="gap-3"><p.Icon className="h-4 w-4 shrink-0" /> {p.label} <ArrowRight className="h-3 w-3 ml-auto opacity-50" /></CommandItem>
          ))}
        </CommandGroup>
        {docs?.length > 0 && (
          <CommandGroup heading={privateMode ? "Documents · searched on this device" : "Documents"}>
            {docs.slice(0, 6).map((d) => (
              <CommandItem key={d.id} value={`doc ${d.title} ${d.category} ${q}`} data-testid={`cmdk-doc-${d.id}`} onSelect={() => go(`/vault?doc=${d.id}`)} className="gap-3">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{d.title}</div><div className="text-[11px] text-muted-foreground">{d.category}{d.summary ? ` · ${d.summary.slice(0, 70)}` : ""}</div></div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {help.length > 0 && (
          <CommandGroup heading="Help & knowledge">
            {help.map((a) => (
              <CommandItem key={a.id} value={`help ${a.title} ${q}`} data-testid={`cmdk-help-${a.id}`} onSelect={() => go(`/help?q=${encodeURIComponent(q)}`)} className="gap-3">
                <LifeBuoy className="h-4 w-4 shrink-0 text-primary" />
                <div className="flex-1 min-w-0"><div className="text-sm truncate">{a.title}</div><div className="text-[11px] text-muted-foreground truncate">{a.body.slice(0, 80)}</div></div>
              </CommandItem>
            ))}
            <CommandItem value={`ask help ${q}`} data-testid="cmdk-help-ask" onSelect={() => go(`/help?q=${encodeURIComponent(q)}`)} className="gap-3 text-primary"><Sparkles className="h-4 w-4 shrink-0" /> Ask the help assistant: “{q}”</CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export const useCommandPalette = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = (e) => e.detail === "search" && setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("hv:open", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("hv:open", onOpen); };
  }, []);
  return [open, setOpen];
};
